import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AccountsApi } from '../../api/accounts.api';
import {
  AccountContract,
  AccountMapContract,
  AccountsListDiffContract,
  AccountValidationContract,
  BaseAccountContract,
  CreateAccountContract,
  UpdateAccountContract,
} from './types';
import { debounceTime, exhaustMap, pipe, switchMap, tap } from 'rxjs';
import { withRequestStatus } from '../../../global/store/features/request-status.feature';
import { Priority } from '../segments/types';
import { parseAccount, parseAccounts } from '../../utils/parse-accounts';
import { PagedResult, PaginationOptions } from '../types';

type AccountsState = {
  accounts: AccountContract[] | undefined;
  accountMap: AccountMapContract[] | undefined;
  accountValidation: AccountValidationContract | undefined;
  accountDetail: AccountContract | undefined;
  accountsListDiff: AccountsListDiffContract | undefined;
  paginatedAccounts: PagedResult<AccountContract> | undefined;
  searchResults: AccountContract[] | undefined;
  countries: string[] | undefined;
  tags: string[] | undefined;
  tagSearchResults: string[] | undefined;
  groups: string[] | undefined;
  groupSearchResults: string[] | undefined;
  accountNumbers: string[] | undefined;
  accountLookupResults: BaseAccountContract[] | undefined;
};

const initialState: AccountsState = {
  accounts: undefined,
  accountMap: undefined,
  accountValidation: undefined,
  accountDetail: undefined,
  accountsListDiff: undefined,
  paginatedAccounts: undefined,
  countries: undefined,
  searchResults: undefined,
  tags: undefined,
  tagSearchResults: undefined,
  groups: undefined,
  groupSearchResults: undefined,
  accountNumbers: undefined,
  accountLookupResults: undefined,
};

const parseAccountMapEntries = (
  entries: AccountMapContract[],
): AccountMapContract[] =>
  entries.map((entry) => ({
    ...entry,
    lastVisitDate: new Date(entry.lastVisitDate),
    idealNextVisitDate: new Date(entry.idealNextVisitDate),
    nextVisitDate: entry.nextVisitDate
      ? new Date(entry.nextVisitDate)
      : undefined,
    snoozeUntil: entry.snoozeUntil ? new Date(entry.snoozeUntil) : undefined,
  }));

export const AccountsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus([
    'getAccountsForCluster',
    'getAccountNumbersForCluster',
    'getAccountMapForCluster',
    'getAccountsForSalesRep',
    'createAccounts',
    'createAccount',
    'validateAccounts',
    'updateAccount',
    'getAccount',
    'getAccountsListDiff',
    'getCountriesForCluster',
    'searchAccounts',
    'searchAccountsInCluster',
    'unsnoozeAccount',
    'getTagsForCluster',
    'getAccountTagsAutocomplete',
    'getAccountGroupsAutocomplete',
    'getGroupsForCluster',
    'lookupAccountsByAccountNumbers',
  ]),
  withMethods((store, accountsApi = inject(AccountsApi)) => ({
    getAccountMapForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
    }>(
      switchMap(({ salesOrganizationId, clusterId }) => {
        return accountsApi
          .getAccountMapForCluster(salesOrganizationId, clusterId)
          .pipe(
            store.handleActionPipe('getAccountMapForCluster', undefined),
            tap((entries) => {
              patchState(store, {
                accountMap: parseAccountMapEntries(entries),
              });
            }),
          );
      }),
    ),
    getAccountsForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      paginationOptions: PaginationOptions;
    }>(
      switchMap(({ salesOrganizationId, clusterId, paginationOptions }) => {
        patchState(store, { accounts: undefined });
        return accountsApi
          .getAccountsForCluster(
            salesOrganizationId,
            clusterId,
            paginationOptions,
          )
          .pipe(
            store.handleActionPipe('getAccountsForCluster', undefined),
            tap((pagedResult) => {
              const { results, count } = pagedResult;
              patchState(store, {
                paginatedAccounts: { results: parseAccounts(results), count },
              });
            }),
          );
      }),
    ),
    getAccountNumbersForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      paginationOptions: Pick<PaginationOptions, 'search' | 'sort'>;
    }>(
      switchMap(({ salesOrganizationId, clusterId, paginationOptions }) => {
        patchState(store, { accountNumbers: undefined });
        return accountsApi
          .getAccountNumbersForCluster(
            salesOrganizationId,
            clusterId,
            paginationOptions,
          )
          .pipe(
            store.handleActionPipe('getAccountNumbersForCluster', undefined),
            tap((accountNumbers) => {
              patchState(store, { accountNumbers });
            }),
          );
      }),
    ),
    getAccountsForSalesRep: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      salesRepId?: string;
    }>(
      switchMap(({ salesOrganizationId, clusterId, salesRepId }) => {
        return accountsApi
          .getAccountsForSalesRepInCluster(
            salesOrganizationId,
            clusterId,
            salesRepId,
          )
          .pipe(
            store.handleActionPipe('getAccountsForSalesRep', undefined),
            tap((accounts) => {
              patchState(store, { accounts: parseAccounts(accounts) });
            }),
          );
      }),
    ),
    getCountriesForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
    }>(
      switchMap(({ salesOrganizationId, clusterId }) => {
        patchState(store, { countries: undefined });
        return accountsApi
          .getCountriesForCluster(salesOrganizationId, clusterId)
          .pipe(
            store.handleActionPipe('getCountriesForCluster', undefined),
            tap((countries) => {
              patchState(store, { countries });
            }),
          );
      }),
    ),
    createAccounts: rxMethod<{
      accounts: CreateAccountContract[];
      salesOrganizationId: string;
      clusterId: string;
      segmentsByPriority?: { [key in Priority]: string[] };
    }>(
      switchMap(
        ({ accounts, salesOrganizationId, clusterId, segmentsByPriority }) => {
          patchState(store, { accounts: undefined });
          return accountsApi
            .createAccounts(
              accounts,
              salesOrganizationId,
              clusterId,
              segmentsByPriority,
            )
            .pipe(
              store.handleActionPipe('createAccounts', undefined),
              tap((accounts) => {
                patchState(store, { accounts: parseAccounts(accounts) });
              }),
            );
        },
      ),
    ),
    updateAccount: rxMethod<UpdateAccountContract>(
      switchMap((account) => {
        return accountsApi.updateAccounts([account]).pipe(
          store.handleActionPipe('updateAccount', undefined),
          tap((accounts) => {
            patchState(store, { accountDetail: parseAccount(accounts[0]) });
          }),
        );
      }),
    ),
    validateAccounts: rxMethod<{
      accounts: CreateAccountContract[];
      salesOrganizationId: string;
      clusterId: string;
    }>(
      switchMap(({ accounts, salesOrganizationId, clusterId }) => {
        patchState(store, { accountValidation: undefined });
        return accountsApi
          .validateAccounts(accounts, salesOrganizationId, clusterId)
          .pipe(
            store.handleActionPipe('validateAccounts', undefined),
            tap((accountValidation) => {
              patchState(store, { accountValidation });
            }),
          );
      }),
    ),
    getAccountsListDiff: rxMethod<{
      accounts: CreateAccountContract[];
      salesOrganizationId: string;
      clusterId: string;
      segmentsByPriority?: { [key in Priority]: string[] };
    }>(
      switchMap(
        ({ accounts, salesOrganizationId, clusterId, segmentsByPriority }) => {
          patchState(store, { accountsListDiff: undefined });
          return accountsApi
            .getAccountsListDiff(
              accounts,
              salesOrganizationId,
              clusterId,
              segmentsByPriority,
            )
            .pipe(
              store.handleActionPipe('getAccountsListDiff', undefined),
              tap((accountsListDiff) => {
                patchState(store, { accountsListDiff });
              }),
            );
        },
      ),
    ),
    searchAccountsForSalesRep: rxMethod<{
      searchString: string;
    }>(
      pipe(
        debounceTime(300),
        switchMap(({ searchString }) => {
          patchState(store, { searchResults: undefined });
          return accountsApi.searchAccountsForSalesRep(searchString).pipe(
            store.handleActionPipe('searchAccounts', undefined),
            tap((accounts) => {
              if (accounts) {
                patchState(store, { searchResults: parseAccounts(accounts) });
              }
            }),
          );
        }),
      ),
    ),
    searchAccountsInCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      query: string;
    }>(
      pipe(
        debounceTime(300),
        switchMap(({ salesOrganizationId, clusterId, query }) => {
          patchState(store, { searchResults: undefined });
          return accountsApi
            .searchAccountsInCluster(salesOrganizationId, clusterId, query)
            .pipe(
              store.handleActionPipe('searchAccountsInCluster', undefined),
              tap((accounts) => {
                if (accounts) {
                  patchState(store, {
                    searchResults: parseAccounts(accounts),
                  });
                }
              }),
            );
        }),
      ),
    ),
    getAccount: rxMethod<string>(
      switchMap((accountId) => {
        patchState(store, { accountDetail: undefined });
        return accountsApi.getAccount(accountId).pipe(
          store.handleActionPipe('getAccount', undefined),
          tap((account) => {
            patchState(store, { accountDetail: parseAccount(account) });
          }),
        );
      }),
    ),
    unsnoozeAccount: rxMethod<string>(
      switchMap((accountId) => {
        return accountsApi.unsnoozeAccount(accountId).pipe(
          store.handleActionPipe('unsnoozeAccount', undefined),
          tap((account) => {
            patchState(store, { accountDetail: parseAccount(account) });
          }),
        );
      }),
    ),
    getTagsForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
    }>(
      switchMap(({ salesOrganizationId, clusterId }) => {
        return accountsApi
          .getTagsForCluster(salesOrganizationId, clusterId)
          .pipe(
            store.handleActionPipe('getTagsForCluster', undefined),
            tap((tags) => patchState(store, { tags })),
          );
      }),
    ),
    getAccountTagsAutocomplete: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      searchString: string;
    }>(
      pipe(
        debounceTime(300),
        switchMap(({ salesOrganizationId, clusterId, searchString }) => {
          patchState(store, { tagSearchResults: undefined });
          return accountsApi
            .getAccountTagsAutocomplete(
              salesOrganizationId,
              clusterId,
              searchString,
            )
            .pipe(
              store.handleActionPipe('getAccountTagsAutocomplete', undefined),
              tap((tags) => {
                if (tags) {
                  patchState(store, { tagSearchResults: tags });
                }
              }),
            );
        }),
      ),
    ),
    getAccountGroupsAutocomplete: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
      searchString: string;
    }>(
      pipe(
        debounceTime(300),
        switchMap(({ salesOrganizationId, clusterId, searchString }) => {
          patchState(store, { groupSearchResults: undefined });
          return accountsApi
            .getAccountGroupsAutocomplete(
              salesOrganizationId,
              clusterId,
              searchString,
            )
            .pipe(
              store.handleActionPipe('getAccountGroupsAutocomplete', undefined),
              tap((groups) => {
                if (groups) {
                  patchState(store, { groupSearchResults: groups });
                }
              }),
            );
        }),
      ),
    ),
    getGroupsForCluster: rxMethod<{
      salesOrganizationId: string;
      clusterId: string;
    }>(
      switchMap(({ salesOrganizationId, clusterId }) => {
        return accountsApi
          .getGroupsForCluster(salesOrganizationId, clusterId)
          .pipe(
            store.handleActionPipe('getGroupsForCluster', undefined),
            tap((groups) => patchState(store, { groups })),
          );
      }),
    ),
    lookupAccountsByAccountNumbers: rxMethod<string[]>(
      switchMap((accountNumbers) => {
        patchState(store, { accountLookupResults: undefined });
        return accountsApi.lookupByAccountNumbers(accountNumbers).pipe(
          store.handleActionPipe('lookupAccountsByAccountNumbers', undefined),
          tap((accountLookupResults) => {
            patchState(store, { accountLookupResults });
          }),
        );
      }),
    ),
    createAccount: rxMethod<{
      account: CreateAccountContract;
      salesOrganizationId: string;
      clusterId: string;
    }>(
      exhaustMap(({ account, salesOrganizationId, clusterId }) => {
        return accountsApi
          .createAccount(account, salesOrganizationId, clusterId)
          .pipe(store.handleActionPipe('createAccount', undefined));
      }),
    ),
  })),
);
